import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { ConfigDefinition, ConfigMetrics } from '@/api/services/config';
import { AdminPageSections, CollapsiblePanel } from '@/components/common';
import {
  SettingsField,
  SettingsGrid,
  SettingsSkeleton,
  SettingsStickyBar,
} from '@/components/settings';
import { ErrorBanner, Toggle } from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { configGridClass, formatConfigLabel } from '@/utils/configLabels';
import { isMaskedSecret } from '@/utils/maskedSecret';

const SECRET_KEYS = new Set([
  'telegram_api_hash',
  'telegram_bot_token',
  'telegram_phone',
  'http_proxy',
  'https_proxy',
]);

const EMAIL_DELIVERY_MODE_OPTIONS = [
  { value: 'disabled', label: 'Disabled — queue only, do not send' },
  { value: 'log', label: 'Log — record locally, do not send' },
  { value: 'fake', label: 'Fake — simulate delivery for testing' },
  { value: 'resend', label: 'Resend — send real email' },
];

const TEST_ACTIONS = [
  { action: 'health_check', label: 'Run Health Check' },
  { action: 'test_database', label: 'Test Database' },
  { action: 'test_redis', label: 'Test Redis' },
  { action: 'test_telegram', label: 'Test Telegram' },
  { action: 'run_cleanup', label: 'Run Cleanup' },
  { action: 'run_diagnostics', label: 'Run Diagnostics' },
  { action: 'run_all_searches', label: 'Run All Searches' },
];

function validateField(defn: ConfigDefinition, value: unknown): string | undefined {
  if (value === '' || value == null) return undefined;
  if (defn.valueType === 'boolean') return undefined;
  if (defn.valueType === 'integer' || defn.valueType === 'float') {
    const n = Number(value);
    if (Number.isNaN(n)) return 'Must be a number';
    if (defn.minValue != null && n < defn.minValue) return `Minimum ${defn.minValue}`;
    if (defn.maxValue != null && n > defn.maxValue) return `Maximum ${defn.maxValue}`;
  }
  return undefined;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function ConfigPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const canManageSecrets = me?.permissions.canManageConfig ?? false;
  const [definitions, setDefinitions] = useState<ConfigDefinition[]>([]);
  const [saved, setSaved] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [metrics, setMetrics] = useState<ConfigMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModified, setFilterModified] = useState(false);
  const [filterNonDefault, setFilterNonDefault] = useState(false);
  const [filterRestartRequired, setFilterRestartRequired] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);

  const isDirty = useMemo(() => !valuesEqual(saved, draft), [saved, draft]);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const defn of definitions) {
      errors[defn.key] = validateField(defn, draft[defn.key]);
    }
    return errors;
  }, [definitions, draft]);

  const hasValidationErrors = Object.values(fieldErrors).some(Boolean);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [defsRes, metricsRes] = await Promise.all([
        api.config.getDefinitions(),
        api.config.getMetrics().catch(() => null),
      ]);
      setDefinitions(defsRes.definitions);
      const values = Object.fromEntries(defsRes.definitions.map((d) => [d.key, d.value]));
      setSaved(values);
      setDraft(values);
      setMetrics(metricsRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, ConfigDefinition[]>();
    for (const defn of definitions) {
      if (!defn.visible) continue;
      const q = searchQuery.trim().toLowerCase();
      if (q && !defn.key.toLowerCase().includes(q) && !defn.description.toLowerCase().includes(q)) {
        continue;
      }
      if (filterModified && valuesEqual(saved[defn.key], draft[defn.key])) continue;
      if (filterNonDefault && defn.isDefault && valuesEqual(draft[defn.key], defn.defaultValue)) continue;
      if (filterRestartRequired && !defn.restartRequired) continue;
      const list = map.get(defn.category) ?? [];
      list.push(defn);
      map.set(defn.category, list);
    }
    return map;
  }, [definitions, searchQuery, filterModified, filterNonDefault, filterRestartRequired, saved, draft]);

  function setField(key: string, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function discard() {
    setDraft(saved);
    toast('Changes discarded', 'info');
  }

  async function save() {
    if (hasValidationErrors) {
      setError('Fix validation errors before saving.');
      return;
    }
    const patch: Record<string, unknown> = {};
    for (const key of Object.keys(draft)) {
      if (!valuesEqual(saved[key], draft[key])) {
        patch[key] = draft[key];
      }
    }
    if (Object.keys(patch).length === 0) {
      toast('No changes to save', 'info');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.config.bulk(patch);
      setSaved(res.data);
      setDraft(res.data);
      toast(
        res.restart_required ? 'Saved — restart required for some settings' : 'Configuration saved',
        'success'
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function applyToApp() {
    setApplying(true);
    try {
      const res = await api.config.reload();
      toast(res.message, 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Reload failed', 'error');
    } finally {
      setApplying(false);
    }
  }

  async function exportConfig() {
    try {
      const data = await api.config.exportJson();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `civic-alert-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Export failed', 'error');
    }
  }

  async function importEnv(e: React.FormEvent) {
    e.preventDefault();
    setImporting(true);
    try {
      const res = await api.config.importEnv(importText);
      toast(`Imported ${res.updated.length} key(s)`, 'success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  async function runTest(action: string) {
    setTesting(action);
    try {
      const res = await api.config.runTest(action);
      toast(res.message, res.status === 'error' ? 'error' : 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Test failed', 'error');
    } finally {
      setTesting(null);
    }
  }

  function renderField(defn: ConfigDefinition) {
    const err = fieldErrors[defn.key];
    const modified = !valuesEqual(saved[defn.key], draft[defn.key]);
    const secret = SECRET_KEYS.has(defn.key);
    const labelText = formatConfigLabel(defn.key, {
      restartRequired: defn.restartRequired,
      modified,
    });

    if (defn.key === 'email_delivery_mode') {
      return (
        <SettingsField
          key={defn.key}
          label={labelText}
          type="select"
          value={draft[defn.key] ?? 'disabled'}
          options={EMAIL_DELIVERY_MODE_OPTIONS}
          onChange={(v) => setField(defn.key, v)}
          hint={defn.description}
          error={err}
          disabled={!defn.editable}
        />
      );
    }

    if (defn.valueType === 'boolean') {
      return (
        <SettingsField
          key={defn.key}
          label={labelText}
          type="checkbox"
          value={Boolean(draft[defn.key])}
          onChange={(v) => setField(defn.key, v)}
          hint={defn.description}
          error={err}
          disabled={!defn.editable}
        />
      );
    }

    if (secret) {
      const raw = draft[defn.key];
      const masked = isMaskedSecret(raw);
      const displayValue = masked ? '' : String(raw ?? '');

      return (
        <SettingsField
          key={defn.key}
          label={labelText}
          secret
          allowCopy={canManageSecrets}
          value={displayValue}
          onChange={(v) => {
            if (masked && v === '') return;
            setField(defn.key, v);
          }}
          hint={defn.description}
          error={err}
          disabled={!defn.editable}
          placeholder={masked ? 'Leave blank to keep' : undefined}
        />
      );
    }

    const inputType =
      defn.valueType === 'integer' || defn.valueType === 'float' ? 'number' : 'text';

    return (
      <SettingsField
        key={defn.key}
        label={labelText}
        type={inputType}
        value={draft[defn.key] ?? ''}
        onChange={(v) => setField(defn.key, v)}
        hint={defn.description}
        error={err}
        disabled={!defn.editable}
      />
    );
  }

  if (loading) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="settings-page config-page">
      <header className="settings-page__header">
        <div className="settings-page__header-text">
          <h1>Configuration</h1>
          <p>
            Database-driven operational settings. Missing keys are bootstrapped automatically on startup.
          </p>
        </div>
        <div className="settings-page__header-actions">
          <button type="button" className="btn" onClick={exportConfig}>
            Export
          </button>
          <button type="button" className="btn" disabled={applying || saving} onClick={applyToApp}>
            {applying ? 'Applying…' : 'Apply to app'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isDirty || saving || hasValidationErrors}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {metrics && (
        <section className="config-status-dashboard" aria-label="Operational metrics">
          <div className="config-status-card">
            <span className="config-status-card__label">Queue</span>
            <strong className="config-status-card__value">{metrics.queueSize}</strong>
          </div>
          <div className="config-status-card">
            <span className="config-status-card__label">Running</span>
            <strong className="config-status-card__value">{metrics.runningJobs}</strong>
          </div>
          <div className="config-status-card">
            <span className="config-status-card__label">Failed</span>
            <strong className="config-status-card__value">{metrics.failedJobs}</strong>
          </div>
          <div className="config-status-card">
            <span className="config-status-card__label">Links/hr</span>
            <strong className="config-status-card__value">{metrics.linksFoundPerHour}</strong>
          </div>
          <div className="config-status-card">
            <span className="config-status-card__label">Msgs/hr</span>
            <strong className="config-status-card__value">{metrics.messagesPerHour}</strong>
          </div>
          <div className="config-status-card">
            <span className="config-status-card__label">Failure rate</span>
            <strong className="config-status-card__value">{metrics.failureRatePercent}%</strong>
          </div>
        </section>
      )}

      <div className="filter-card config-toolbar">
        <input
          type="search"
          className="config-search filter-card__search"
          placeholder="Search configuration…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search configuration"
        />
        <Toggle
          compact
          checked={filterModified}
          onChange={setFilterModified}
          label="Modified"
        />
        <Toggle
          compact
          checked={filterNonDefault}
          onChange={setFilterNonDefault}
          label="Non-default"
        />
        <Toggle
          compact
          checked={filterRestartRequired}
          onChange={setFilterRestartRequired}
          label="Restart required"
        />
      </div>

      <AdminPageSections className="config-page-sections">
        {Array.from(grouped.entries()).map(([category, items]) => (
          <CollapsiblePanel
            key={category}
            id={`config-${category}`}
            storageScope="config-registry"
            title={items[0]?.categoryLabel ?? category}
            defaultExpanded={category === 'telegram' || category === 'email' || category === 'scheduler'}
          >
            <SettingsGrid className={configGridClass(category)}>
              {items.map((defn) => renderField(defn))}
            </SettingsGrid>
          </CollapsiblePanel>
        ))}

        <CollapsiblePanel
          id="config-system-tests"
          storageScope="config-registry"
          title="System tests"
          subtitle="Run diagnostics without changing code."
          defaultExpanded={false}
        >
          <div className="config-test-grid">
              {TEST_ACTIONS.map(({ action, label }) => (
                <button
                  key={action}
                  type="button"
                  className="btn"
                  disabled={testing !== null}
                  onClick={() => runTest(action)}
                >
                  {testing === action ? 'Running…' : label}
                </button>
              ))}
          </div>
        </CollapsiblePanel>

        <CollapsiblePanel
          id="config-import-env"
          storageScope="config-registry"
          title="Import from .env"
          defaultExpanded={false}
        >
          <form onSubmit={importEnv}>
            <textarea
              className="config-import-textarea"
              rows={8}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="TELEGRAM_BOT_TOKEN=…&#10;COLLECTOR_INTERVAL_MINUTES=60"
            />
            <button type="submit" className="btn btn-primary" disabled={importing || !importText.trim()}>
              {importing ? 'Importing…' : 'Import env block'}
            </button>
          </form>
        </CollapsiblePanel>
      </AdminPageSections>

      <SettingsStickyBar
        visible={isDirty}
        saving={saving}
        onSave={save}
        onDiscard={discard}
      />
    </div>
  );
}
